import type { VesselTcContractFragment } from '../../graphql';
import { assertDefined } from '../../lib/util';
import { generateClientId } from './ClientId';
import {
  EventCategory,
  type TcInEntry,
  type TcInEntryContractDetails,
} from './Core';
import type { ScheduleDataContext } from './ScheduleDataContext';
import type { TcInEvent } from './TimeData';
import { CoreEventType } from './TimeData';
import { defaultConstraints } from './util';
import { dToMs } from '../../lib/time';

export function getSortedTcInEvents(
  tcContracts: VesselTcContractFragment[],
  scheduleContext: ScheduleDataContext,
): TcInEvent[] {
  if (tcContracts.length < 1) return [];

  const tcInEntryContractDetails = tcContracts
    .map((tc) => vesselTcContractNodeToTcInEntryContractDetails(tc))
    .sort((a, b) => a.deliveryStartDate - b.deliveryStartDate);

  const tcInEntries = buildTcInEntries(tcInEntryContractDetails);

  return tcInEntries.map((entry) =>
    buildStaticEventsFromTcInEntry(entry, scheduleContext),
  );
}

function buildTcInEntries(contracts: TcInEntryContractDetails[]): TcInEntry[] {
  if (contracts.length === 0) {
    return [];
  }

  const entries: TcInEntry[] = [];

  const firstContract = contracts[0];

  // First entry
  entries.push({
    category: EventCategory.Static,
    clientId: generateClientId(),
    dbId: null,
    updatedAt: null,
    delivery: {
      endDate: firstContract.deliveryEndDate,
      locationId: firstContract.deliveryPortId,
      startDate: firstContract.deliveryStartDate,
      tcContractDbId: firstContract.dbId,
      tcContractExternalId: firstContract.externalId,
    },
    redelivery: null,
    vesselId: contracts[0].vesselId,
  });

  for (let i = 0; i < contracts.length; i++) {
    const redeliveryContract = contracts[i];
    const deliveryContract = contracts.at(i + 1);

    entries.push({
      category: EventCategory.Static,
      clientId: generateClientId(),
      dbId: null,
      updatedAt: null,
      delivery: deliveryContract
        ? {
            endDate: deliveryContract.deliveryEndDate,
            locationId: deliveryContract.deliveryPortId,
            startDate: deliveryContract.deliveryStartDate,
            tcContractDbId: deliveryContract.dbId,
            tcContractExternalId: deliveryContract.externalId,
          }
        : null,
      redelivery: {
        endDate: redeliveryContract.redeliveryEndDate,
        locationId: redeliveryContract.redeliveryPortId,
        startDate: redeliveryContract.redeliveryStartDate,
        tcContractDbId: redeliveryContract.dbId,
        tcContractExternalId: redeliveryContract.externalId,
      },
      vesselId: firstContract.vesselId,
    });
  }

  return entries;
}

function vesselTcContractNodeToTcInEntryContractDetails(
  tc: VesselTcContractFragment,
): TcInEntryContractDetails {
  return {
    dbId: tc.id,
    deliveryEndDate: new Date(tc.deliveryEndDate).getTime(),
    deliveryPortId: tc.deliveryPortId,
    deliveryStartDate: new Date(tc.deliveryStartDate).getTime(),
    externalId: tc.externalId,
    redeliveryEndDate: new Date(tc.redeliveryEndDate).getTime(),
    redeliveryPortId: tc.redeliveryPortId,
    redeliveryStartDate: new Date(tc.redeliveryStartDate).getTime(),
    vesselId: tc.vesselId,
  };
}

function buildStaticEventsFromTcInEntry(
  entry: TcInEntry,
  scheduleContext: ScheduleDataContext,
): TcInEvent {
  const vessel = scheduleContext.vessels.get(entry.vesselId);

  let locationA = entry.redelivery
    ? scheduleContext.locations.get(entry.redelivery.locationId)
    : null;
  assertDefined(
    entry.redelivery ? locationA : true,
    'location data is expected to exist for given entry.redelivery.locationId',
  );

  let locationB = entry.delivery
    ? scheduleContext.locations.get(entry.delivery.locationId)
    : null;
  assertDefined(
    entry.delivery ? locationB : true,
    'location data is expected to exist for given entry.delivery.locationId',
  );

  // For convenience, if one location is missing, use the other one
  locationA = locationA ?? locationB;
  locationB = locationB ?? locationA;

  assertDefined(locationA, 'locationA data is expected to exist');
  assertDefined(locationB, 'locationB data is expected to exist');
  assertDefined(vessel, 'vessel data is expected to exist for given vesselId');

  const { delivery, redelivery } = entry;

  let startDate = redelivery?.startDate ?? null;
  let endDate = delivery?.endDate ?? null;

  assertDefined(
    startDate ?? endDate,
    'startDate or endDate is expected to exist',
  );

  startDate = startDate !== null ? startDate : endDate! - dToMs(7);
  endDate = endDate !== null ? endDate : startDate + dToMs(7);

  const duration = endDate - startDate;

  return {
    category: EventCategory.Static,
    constraints: defaultConstraints,
    duration,
    end: endDate,
    entry,
    locationAId: locationA.id,
    locationAName: locationA.name,
    locationATimezone: locationA.timezone ?? 'UTC',
    locationBId: locationB.id,
    locationBName: locationB.name,
    locationBTimezone: locationB.timezone ?? 'UTC',
    portCallId: null,
    seaDurationOverride: null,
    start: startDate,
    startOverride: null,
    staticEventType: 'TC_IN',
    type: CoreEventType.Static,
    vessel,
    vesselId: entry.vesselId,
  };
}

/**
 * Checks whether the given TC In event is the first known TC In event for the vessel
 *
 * The first TC In event has do redelivery defined
 */
export function isFirstTcInEvent(
  event: TcInEvent,
): event is TcInEvent & { redelivery: null } {
  return event.entry.redelivery === null;
}

/**
 * Checks whether the given TC In event is the last known TC In event for the vessel
 *
 * The last TC In event has do delivery defined
 */
export function isLastTcInEvent(
  event: TcInEvent,
): event is TcInEvent & { delivery: null } {
  return event.entry.delivery === null;
}
